import { useMemo, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { BsExclamationLg } from 'react-icons/bs';
import { FaCheck, FaXmark } from 'react-icons/fa6';

import {
  getToastSnapshot,
  subscribeToasts,
  toast,
} from '@/components/toastStore';

const SuccessIcon = () => {
  return (
    <span className="bg-success flex size-4 items-center justify-center rounded-full">
      <FaCheck className="size-2.5 text-white" aria-hidden="true" />
    </span>
  );
};

const ErrorIcon = () => {
  return (
    <span className="bg-error flex size-4 items-center justify-center rounded-full">
      <FaXmark className="size-2.5 text-white" aria-hidden="true" />
    </span>
  );
};

const WarningIcon = () => {
  return (
    <span className="bg-warning flex size-4 items-center justify-center rounded-full">
      <BsExclamationLg className="h-3.5 w-3 text-white" aria-hidden="true" />
    </span>
  );
};

interface ToastViewportProps {
  className?: string;
}

const ToastViewport = ({ className }: ToastViewportProps) => {
  const items = useSyncExternalStore(
    subscribeToasts,
    getToastSnapshot,
    getToastSnapshot,
  );

  const content = useMemo(() => {
    return (
      <div
        className={
          'pointer-events-none fixed inset-x-0 top-4 z-50 px-4 ' +
          (className ?? '')
        }
        aria-live="polite"
        aria-relevant="additions removals"
      >
        <div className="mx-auto flex max-w-md flex-col items-center gap-2">
          {items.map((t) => {
            const icon =
              t.type === 'success' ? (
                <SuccessIcon />
              ) : t.type === 'warning' ? (
                <WarningIcon />
              ) : (
                <ErrorIcon />
              );
            const animationClass = t.isExiting
              ? 'animate-toast-out'
              : 'animate-toast-in';

            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toast.dismiss(t.id)}
                className={
                  'shadow-toast backdrop-blur-2 bg-toast-bg/70 pointer-events-auto flex h-9 items-center justify-center gap-1.5 rounded-full px-2 py-1 text-white transition hover:brightness-105 active:brightness-95 ' +
                  animationClass
                }
                role="status"
              >
                <span className="shrink-0">{icon}</span>
                <span className="leading-toast text-center text-sm">
                  {t.message}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }, [className, items]);

  if (typeof document === 'undefined') return null;
  return createPortal(content, document.body);
};

export default ToastViewport;
